import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { LiveChannel } from '@lumos/contracts';
import { ApiError, describeError } from '../api/client.js';
import { addFavorite, createChannelSession, removeFavorite } from '../api/endpoints.js';
import { LiveIcon, LumosMark } from '../components/icons.js';
import { RetryError } from '../components/Placeholders.js';
import { StarGlyph } from '../livetv/ChannelLogo.js';
import { ProgramDetail } from '../livetv/ProgramDetail.js';
import { TileGrid } from '../livetv/TileGrid.js';
import {
  channelGroups,
  channelsInView,
  loadLiveChannels,
  rememberFavorite,
  sameView,
  viewKey,
  type ChannelView,
} from '../livetv/store.js';
import './livetv.css';

/** Шире — панель канала колонкой справа; уже — листом (снизу, а с 900 — справа). */
const PANEL_QUERY = '(min-width: 1200px)';
const SHEET_MS = 240;

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (): void => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function panelWide(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(PANEL_QUERY).matches;
}

/**
 * ТВ: каналы плейлиста плитками.
 *
 * Слева — сетка плиток (логотип, название, что идёт, полоса эфира и звезда
 * избранного в углу), сверху — группы плейлиста, «Избранное» первой. Справа —
 * справочная панель выбранного канала: что идёт, описание и «Сейчас / Далее»,
 * без кнопок. Смотреть — нажатием на плитку: щелчок мыши и Enter запускают
 * сразу, касание сначала выбирает (панель или, уже 1200 точек, лист), а
 * повторное — запускает.
 *
 * Каналов у Skaz под две тысячи: сетка рисует только видимые строки, а
 * «сейчас/далее» спрашивают пакетами только для видимых плиток
 * (`livetv/store.ts`). Клиент знает только наш id канала: адрес потока
 * подставляет сервер при создании сессии.
 */
export function LiveTvPage(): React.JSX.Element {
  const [channels, setChannels] = useState<LiveChannel[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Список каналов не пришёл — отдельно от прочих ошибок: только тут есть что повторять. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  /** `null` — зритель ещё не выбирал, и раздел решает сам (см. `view`). */
  const [selection, setSelection] = useState<ChannelView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const panelMode = useMedia(PANEL_QUERY);

  useEffect(() => {
    const abort = new AbortController();
    setLoadError(null);
    loadLiveChannels({ fresh: true, signal: abort.signal })
      .then((response) => {
        setChannels(response.channels);
        setNotice(response.notice);
      })
      .catch((e: unknown) => {
        if (abort.signal.aborted) return;
        setLoadError(e instanceof ApiError || e instanceof Error ? describeError(e) : 'Каналы недоступны.');
      });
    return () => abort.abort();
  }, [attempt]);

  const groups = useMemo(() => channelGroups(channels ?? []), [channels]);
  const favoriteCount = useMemo(() => (channels ?? []).filter((channel) => channel.favorite).length, [channels]);

  // Умолчание: свои каналы, а если их нет — первая группа, а не «Все»: в
  // одной группе каналы знакомые, а не первые из двух тысяч.
  const firstGroup = groups[0];
  const hasFavorites = favoriteCount > 0;
  const view = useMemo<ChannelView>(
    () =>
      selection ?? (
        hasFavorites
          ? { kind: 'favorites' }
          : firstGroup !== undefined
            ? { kind: 'group', group: firstGroup }
            : { kind: 'all' }
      ),
    [selection, hasFavorites, firstGroup],
  );
  const viewId = viewKey(view);
  const viewRef = useRef(view);
  viewRef.current = view;
  const visible = useMemo(() => channelsInView(channels ?? [], view), [channels, view]);

  /** Выбранный канал. Нет выбора или канал ушёл из вида — первый канал вида. */
  const [pickedId, setPickedId] = useState<string | null>(null);
  const chosenChannel = (pickedId === null ? undefined : visible.find((channel) => channel.id === pickedId))
    ?? visible[0]
    ?? null;
  const chosenId = useRef<string | null>(null);
  chosenId.current = chosenChannel?.id ?? null;

  /** Лист с панелью (узкий экран) и куда вернуть фокус после него. */
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetOpenRef = useRef(false);
  sheetOpenRef.current = sheetOpen;
  const sheetReturn = useRef<HTMLElement | null>(null);
  const closeSheet = useCallback((): void => {
    setSheetOpen(false);
    sheetReturn.current?.focus({ preventScroll: true });
  }, []);

  /**
   * Звёздочка. Состояние переключается сразу, не дожидаясь сети: задержка в
   * полсекунды читается как «не нажалось». Неудача возвращает всё на место.
   */
  const toggleFavorite = useCallback(async (channel: LiveChannel): Promise<void> => {
    const next = !channel.favorite;
    const mark = (value: boolean): void => {
      rememberFavorite(channel.id, value);
      setChannels((prev) =>
        prev === null ? prev : prev.map((entry) => (entry.id === channel.id ? { ...entry, favorite: value } : entry)),
      );
    };
    // Вид закрепляется за тем, что на экране: первая звёздочка иначе сама
    // перебросила бы зрителя из группы в «Избранное».
    setSelection((prev) => prev ?? viewRef.current);
    mark(next);
    setError(null);
    try {
      if (next) await addFavorite(channel.contentId);
      else await removeFavorite(channel.contentId);
    } catch {
      mark(!next);
      setError('Не удалось изменить избранное.');
    }
  }, []);

  const busyRef = useRef(false);
  const watch = useCallback(async (channel: LiveChannel): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(channel.id);
    setError(null);
    try {
      const session = await createChannelSession(channel.id);
      window.location.hash = `#/watch/${encodeURIComponent(session.sessionId)}`;
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? describeError(e) : 'Канал не запустился.');
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }, []);

  const watchFromSheet = useCallback((channel: LiveChannel): void => {
    void watch(channel);
  }, [watch]);

  /** Фокус с клавиатуры или задержавшаяся мышь: панель показывает этот канал. */
  const pick = useCallback((channel: LiveChannel): void => {
    setPickedId(channel.id);
  }, []);

  /**
   * Нажатие на плитку. Мышь и Enter — смотреть сразу. Касание: первое выбирает
   * (с панелью рядом — в панели, без неё — листом), повторное по выбранной —
   * смотреть.
   */
  const activate = useCallback((channel: LiveChannel, touch: boolean): void => {
    const chosen = chosenId.current === channel.id;
    setPickedId(channel.id);
    if (!touch || (chosen && (panelWide() || sheetOpenRef.current))) {
      void watch(channel);
      return;
    }
    if (panelWide()) return;
    if (!sheetOpenRef.current) {
      sheetReturn.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    setSheetOpen(true);
  }, [watch]);

  const starFavorite = useCallback((channel: LiveChannel): void => {
    void toggleFavorite(channel);
  }, [toggleFavorite]);

  /**
   * Касание мимо листа: пришлось на плитку — это нажатие на неё (выбранная —
   * смотреть, другая — показать её в листе), иначе лист закрывается.
   */
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const tapThrough = useCallback((x: number, y: number): boolean => {
    if (typeof document.elementsFromPoint !== 'function') return false;
    const tile = document.elementsFromPoint(x, y).find((element) => element.classList.contains('tv-tile'));
    if (!(tile instanceof HTMLElement)) return false;
    const channel = visibleRef.current[Number(tile.dataset.index)];
    if (channel === undefined) return false;
    activate(channel, true);
    return true;
  }, [activate]);

  const choose = useCallback((next: ChannelView): void => {
    setSelection(next);
    setPickedId(null);
  }, []);

  const focusTiles = useCallback((): void => {
    document.querySelector<HTMLElement>('.tv-tile[tabindex="0"]')?.focus();
  }, []);
  const focusGroups = useCallback((): void => {
    document.querySelector<HTMLElement>('.tv-group.active')?.focus();
  }, []);
  const focusPanel = useCallback((): void => {
    document.querySelector<HTMLElement>('.tv-side .tv-next-list')?.focus();
  }, []);

  const loading = channels === null && loadError === null;
  const hasList = channels !== null && channels.length > 0;
  const showPanel = panelMode && chosenChannel !== null;

  return (
    <main className={['app', 'tv-page', panelMode ? 'is-wide' : 'is-narrow', showPanel ? 'has-panel' : ''].join(' ').trim()}>
      <div className="tv-main">
        <header className="header tv-header">
          <span className="brand">
            <LumosMark className="brand-mark" />
            Lumos
          </span>
          <h1 className="page-title header-title">ТВ</h1>
        </header>

        {loadError !== null && <RetryError message={loadError} onRetry={() => setAttempt((value) => value + 1)} />}
        {error !== null && <p className="state error tv-error">{error}</p>}
        {/* «Провайдер не отвечает, список по памяти» — и над непустым списком:
            каналы на экране есть, а включаться они, скорее всего, не будут. */}
        {hasList && notice !== null && <p className="catalog-notice tv-notice">{notice}</p>}

        {channels !== null && channels.length === 0 && (
          <div className="empty-state" role="status">
            <div className="empty-state-icon" aria-hidden="true">
              <LiveIcon />
            </div>
            <h2>Каналов пока нет</h2>
            <p>{notice ?? 'Плейлист каналов не настроен или пуст.'}</p>
          </div>
        )}

        {hasList && (
          <TvGroups
            groups={groups}
            view={view}
            favoriteCount={favoriteCount}
            onChoose={choose}
            onDown={focusTiles}
          />
        )}

        {loading && <TilesSkeleton />}

        {hasList && visible.length === 0 && view.kind === 'favorites' && (
          <div className="tv-empty" role="status">
            <StarGlyph filled={false} className="tv-empty-icon" />
            <p>Отметьте каналы звёздочкой — они соберутся здесь.</p>
          </div>
        )}

        {visible.length > 0 && (
          <TileGrid
            key={`${viewId}:${panelMode ? 'wide' : 'narrow'}`}
            channels={visible}
            selectedId={chosenChannel?.id ?? null}
            busyId={busy}
            ownScroll={panelMode}
            onPick={pick}
            onActivate={activate}
            onToggleFavorite={starFavorite}
            onLeaveTop={focusGroups}
            {...(showPanel ? { onLeaveRight: focusPanel } : {})}
          />
        )}
      </div>

      {showPanel && chosenChannel !== null && (
        <aside className="tv-side" aria-label="Выбранный канал">
          <ProgramDetail channel={chosenChannel} busy={busy === chosenChannel.id} onLeaveLeft={focusTiles} />
        </aside>
      )}

      {!showPanel && (
        <ProgramSheet open={sheetOpen && chosenChannel !== null} onClose={closeSheet} onTapOutside={tapThrough}>
          {chosenChannel !== null && (
            <ProgramDetail
              channel={chosenChannel}
              busy={busy === chosenChannel.id}
              onTapWatch={watchFromSheet}
              onClose={closeSheet}
            />
          )}
        </ProgramSheet>
      )}
    </main>
  );
}

/* ---------- Лист панели (узкий экран) ---------- */

/**
 * Панель канала листом снизу (с 900 точек — справа). Выезжает сдвигом (transform), подложка —
 * прозрачностью; закрывается крестиком, касанием мимо и Esc. Касание мимо,
 * пришедшееся на плитку, — нажатие на неё (`onTapOutside` вернёт `true`).
 */
function ProgramSheet({
  open,
  onClose,
  onTapOutside,
  children,
}: {
  open: boolean;
  onClose: () => void;
  onTapOutside: (x: number, y: number) => boolean;
  children: React.ReactNode;
}): React.JSX.Element | null {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), SHEET_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!shown) return;
    sheetRef.current?.querySelector<HTMLElement>('.tv-detail-close')?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown, onClose]);

  if (!mounted) return null;
  return (
    <div className={shown ? 'tv-sheet-layer is-open' : 'tv-sheet-layer'}>
      <div
        className="tv-sheet-scrim"
        onClick={(event) => {
          if (!onTapOutside(event.clientX, event.clientY)) onClose();
        }}
        aria-hidden="true"
      />
      <div className="tv-sheet" role="dialog" aria-modal="true" aria-label="Канал" ref={sheetRef}>
        {children}
      </div>
    </div>
  );
}

/* ---------- Группы ---------- */

/**
 * Группы-капсулы одной строкой: их бывает три десятка, и в несколько строк
 * они отодвигали бы плитки. Не влезло — строка прокручивается вбок. Стрелки
 * переходят между капсулами, «вниз» — к плиткам. Счётчиков у капсул нет:
 * «1 800» ничего не говорит зрителю и только шумит.
 */
const TvGroups = memo(function TvGroups({
  groups,
  view,
  favoriteCount,
  onChoose,
  onDown,
}: {
  groups: string[];
  view: ChannelView;
  favoriteCount: number;
  onChoose: (view: ChannelView) => void;
  onDown: () => void;
}): React.JSX.Element {
  const options: { view: ChannelView; label: React.ReactNode }[] = [
    {
      view: { kind: 'favorites' },
      label: (
        <>
          <StarGlyph filled={favoriteCount > 0} className="tv-group-star" />
          Избранное
        </>
      ),
    },
    { view: { kind: 'all' }, label: 'Все' },
    ...groups.map((group) => ({ view: { kind: 'group', group } as ChannelView, label: group })),
  ];
  const rowRef = useRef<HTMLDivElement | null>(null);

  // Выбранная группа может стоять в хвосте строки — подводим её в видимую часть.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const active = row?.querySelector<HTMLElement>('.tv-group.active');
    if (!row || !active) return;
    const left = active.offsetLeft - row.offsetLeft;
    if (left < row.scrollLeft || left + active.offsetWidth > row.scrollLeft + row.clientWidth) {
      // Запас — поле ленты, где край растворяется: выбранная капсула встаёт за ним.
      row.scrollLeft = left - 28;
    }
  }, [view]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const buttons = [...(rowRef.current?.querySelectorAll<HTMLButtonElement>('.tv-group') ?? [])];
    const at = buttons.indexOf(event.target as HTMLButtonElement);
    if (at < 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const target = buttons[at + (event.key === 'ArrowRight' ? 1 : -1)];
      if (target === undefined) return;
      event.preventDefault();
      target.focus();
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      onDown();
    }
  };

  return (
    <div className="tv-groups" ref={rowRef} role="tablist" aria-label="Группы каналов" onKeyDown={onKeyDown}>
      {options.map((option) => {
        const active = sameView(option.view, view);
        return (
          <button
            key={viewKey(option.view)}
            type="button"
            role="tab"
            aria-selected={active}
            className={active ? 'tv-group active' : 'tv-group'}
            onClick={() => onChoose(option.view)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
});

/* ---------- Загрузка ---------- */

function TilesSkeleton(): React.JSX.Element {
  return (
    <div className="tv-skel" aria-busy="true" aria-label="Загрузка каналов…">
      {Array.from({ length: 12 }, (_, index) => (
        <span key={index} className="skeleton-block tv-skel-tile" />
      ))}
    </div>
  );
}
